/**
 * Formatting for the restaurant Dashboard — en-US, restaurant-local dates.
 *
 * Dates from the API are LOCAL calendar dates ("2026-09-22"), so they are
 * formatted as calendar dates (at noon UTC, never shifted by the viewer's
 * zone). Instants (placedAt, generatedAt) are formatted in the restaurant's
 * timezone.
 */

export const DEFAULT_TZ = 'America/New_York'

const usd0 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', maximumFractionDigits: 0 })
const usd2 = new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2, maximumFractionDigits: 2 })
const int = new Intl.NumberFormat('en-US')

export const money = n => (n == null || Number.isNaN(Number(n)) ? '—' : usd2.format(Number(n)))
export const moneyShort = n => (n == null ? '—' : Math.abs(n) >= 1000 ? usd0.format(n) : usd2.format(n))
export const moneyAxis = n => usd0.format(n)
export const count = n => (n == null ? '—' : int.format(n))

/** Today's calendar date in `tz`, as YYYY-MM-DD. */
export function todayIn(tz = DEFAULT_TZ) {
  const p = {}
  for (const part of new Intl.DateTimeFormat('en-US', { timeZone: tz, year: 'numeric', month: '2-digit', day: '2-digit' }).formatToParts(new Date())) p[part.type] = part.value
  return `${p.year}-${p.month}-${p.day}`
}

export function addDays(dateStr, days) {
  const [y, m, d] = dateStr.split('-').map(Number)
  const dt = new Date(Date.UTC(y, m - 1, d))
  dt.setUTCDate(dt.getUTCDate() + days)
  return dt.toISOString().slice(0, 10)
}

export function spanDays(from, to) {
  return Math.round((Date.parse(`${to}T00:00:00Z`) - Date.parse(`${from}T00:00:00Z`)) / 86400000) + 1
}

const asNoonUtc = s => new Date(`${s}T12:00:00Z`)
export const dayShort = s => (s ? new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', timeZone: 'UTC' }).format(asNoonUtc(s)) : '')
export const dayLong = s => (s ? new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric', year: 'numeric', timeZone: 'UTC' }).format(asNoonUtc(s)) : '')
export const dayWithWeekday = s => (s ? new Intl.DateTimeFormat('en-US', { weekday: 'short', month: 'short', day: 'numeric', timeZone: 'UTC' }).format(asNoonUtc(s)) : '')

export function rangeLabel(from, to) {
  if (!from || !to) return ''
  if (from === to) return dayWithWeekday(from)
  return `${dayWithWeekday(from)} – ${dayWithWeekday(to)}, ${to.slice(0, 4)}`
}

export const longToday = (tz = DEFAULT_TZ) =>
  new Intl.DateTimeFormat('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: tz }).format(new Date())

export const timeIn = (iso, tz = DEFAULT_TZ) =>
  iso ? new Intl.DateTimeFormat('en-US', { hour: 'numeric', minute: '2-digit', timeZone: tz, timeZoneName: 'short' }).format(new Date(iso)) : ''

export const dateTimeIn = (iso, tz = DEFAULT_TZ) =>
  iso ? new Intl.DateTimeFormat('en-US', { month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit', timeZone: tz }).format(new Date(iso)) : ''

export const hourLabel = h => (h === 0 ? '12am' : h < 12 ? `${h}am` : h === 12 ? '12pm' : `${h - 12}pm`)
export const hourLong = h => (h === 0 ? '12 AM' : h < 12 ? `${h} AM` : h === 12 ? '12 PM' : `${h - 12} PM`)

/**
 * Change between periods, as a percentage. null when there is nothing to
 * compare against — a rise from zero is not an infinite percent.
 */
export function delta(cur, prev) {
  if (prev == null || cur == null) return null
  if (Number(prev) === 0) return Number(cur) === 0 ? 0 : null
  return ((Number(cur) - Number(prev)) / Math.abs(Number(prev))) * 100
}

export const pct = n => `${Math.abs(n) >= 10 ? Math.abs(n).toFixed(0) : Math.abs(n).toFixed(1)}%`

/** "vs previous 7 days" and friends — the label under every delta. */
export function compareLabel(compare, from, to) {
  if (compare === 'none') return ''
  if (compare === 'year') return 'vs same period last year'
  const n = spanDays(from, to)
  return n === 1 ? 'vs previous day' : `vs previous ${n} days`
}

/** Download rows as a CSV file. Values are quoted so names with commas survive. */
export function downloadCsv(filename, rows) {
  const esc = v => {
    const s = v == null ? '' : String(v)
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s
  }
  const csv = rows.map(r => r.map(esc).join(',')).join('\n')
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8' })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  setTimeout(() => URL.revokeObjectURL(url), 1000)
}
